/**
 * Der Listenkörper der Dokumentliste (Spec 09, Spec 35, Spec 36): Spaltenkopf,
 * optimistische Zeile und die Dokumente nach Tag/Monat gegliedert (Gruppe vom
 * Server). Rein darstellend — Abfrage, Blatt und „Mehr laden" gehören
 * `DocumentList`. Bereich Dokumente und Facet am Objekt teilen sich diesen
 * Körper.
 */

import { Fragment } from 'react'

import { ASSIGN_PLACEHOLDER_CLASS, DOCUMENT_COLUMN, DocumentRow } from '@/features/documents/DocumentRow'
import { groupDocuments, groupTestId } from '@/features/documents/list-groups'
import type { DocumentRow as DocumentRowData } from '@/features/documents/api'

interface DocumentListBodyProps {
  readonly documents: readonly DocumentRowData[]
  /** Mit Suchbegriff sortiert der Server nach Relevanz — dann keine
   * Gruppenüberschriften (Spec 35, Fachregel 2). */
  readonly isSearching: boolean
  readonly pendingFileName: string | null
  /** Öffnet das Blatt zum Ändern; auch für `document-assign-<id>`. */
  readonly onSelect: (document: DocumentRowData) => void
  /** Karten ohne Spaltenkopf, auch am Schreibtisch (`DocumentRow.compact`). */
  readonly compact?: boolean
}

export function DocumentListBody({ documents, isSearching, pendingFileName, onSelect, compact = false }: DocumentListBodyProps) {
  const groups = groupDocuments(documents, isSearching)
  return (
    <>
      {!compact && <DocumentListHeader />}
      <ul>
        {pendingFileName !== null && <PendingRow fileName={pendingFileName} />}
        {groups.map((group) => (
          <Fragment key={group.key ?? 'search'}>
            {group.key !== null && <GroupHeading groupKey={group.key} heading={group.heading} />}
            {group.items.map((document) => (
              <DocumentRow
                key={document.id}
                document={document}
                compact={compact}
                onOpen={() => {
                  onSelect(document)
                }}
                onAssign={() => {
                  onSelect(document)
                }}
              />
            ))}
          </Fragment>
        ))}
      </ul>
    </>
  )
}

/** Spec 35/36: Gruppenzeile zwischen den Dokumenten (`document-day-<JJJJ-MM-TT>`
 * bzw. `document-month-<JJJJ-MM>`, Bezeichner in beiden Clients gleich; Text =
 * `list_group.label` vom Server). Am Schreibtisch eine Zwischenzeile, schmal
 * die Überschrift über den Karten — dieselbe Zeile, Stil wie die
 * Gruppenüberschriften der Sidebar. */
function GroupHeading({ groupKey, heading }: { groupKey: string; heading: string | null }) {
  return (
    <li data-testid={groupTestId(groupKey)} className="px-(--page-x) pt-4 pb-1">
      <h2 className="text-label text-muted font-medium tracking-wide uppercase">{heading}</h2>
    </li>
  )
}

/** Kein `data-testid` — ein Spaltenkopf ist reine Beschriftung, kein
 * Testziel (dieselbe Begründung wie `OpenItemsHeader`). */
function DocumentListHeader() {
  return (
    <div className="border-border bg-bg text-label text-muted sticky top-0 z-10 hidden items-center border-b px-(--page-x) py-1 md:flex">
      <div className="flex flex-1 items-center gap-3">
        <span className={`text-center ${DOCUMENT_COLUMN.symbol}`}></span>
        <span className={DOCUMENT_COLUMN.document}>Dokument</span>
        <span className={DOCUMENT_COLUMN.subject}>Hängt an</span>
        <span className={DOCUMENT_COLUMN.date}>Datum</span>
        <span data-numeric className={DOCUMENT_COLUMN.amount}>
          Betrag
        </span>
        <span className={DOCUMENT_COLUMN.hint}>Hinweis</span>
      </div>
      {/* Reserviert dieselbe Breite wie `document-assign-<id>`/sein
       * Platzhalter in jeder Zeile (W-D2) — sonst bleibt der Kopf so breit
       * wie eine zugeordnete Zeile ohne diese Spalte, während jede
       * unzugeordnete Zeile schmaler ist. */}
      <span aria-hidden="true" className={ASSIGN_PLACEHOLDER_CLASS}>
        Zuordnen…
      </span>
    </div>
  )
}

/** `document-row-pending` (Bauform aus 02b, Spec 09 „Während der Text
 * gelesen wird"): Fortschritt an der optimistischen Zeile, solange Upload,
 * Textlesen **und** `POST /documents` laufen. */
function PendingRow({ fileName }: { fileName: string }) {
  return (
    <li
      data-testid="document-row-pending"
      aria-busy="true"
      className="border-border text-label text-muted flex min-h-(--row-h) items-center gap-3 border-b px-(--page-x) py-2"
    >
      <span className="bg-border size-2 shrink-0 animate-pulse rounded-full" aria-hidden="true" />
      <span className="truncate">{fileName} wird abgelegt …</span>
    </li>
  )
}
